import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import {
  useCreateSquad,
  useJoinSquad,
  getGetCurrentUserQueryKey,
  getListSquadsQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useAuth } from "@clerk/react";

type UnclaimedMember = { id: number; name: string };
type SquadLookup = { squad: { id: number; name: string }; unclaimedMembers: UnclaimedMember[] };

// WI#19 — startOnCreate is true when the server says this user may create a squad right now
// (someone who has just paid for Squad or Coach Pro). They bought a squad, so they land on
// "Create your squad" instead of a three-way chooser; the chooser stays one click away.
export default function SetupProfile({ startOnCreate = false }: { startOnCreate?: boolean }) {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { getToken } = useAuth();
  const createSquad = useCreateSquad();
  const joinSquad = useJoinSquad();

  const [mode, setMode] = useState<"choose" | "create" | "join" | "claim" | "claim-pick" | "welcome">(
    startOnCreate ? "create" : "choose",
  );
  const [buyerLanding, setBuyerLanding] = useState(startOnCreate);
  const [name, setName] = useState("");
  const [goalRace, setGoalRace] = useState("");
  const [squadName, setSquadName] = useState("");
  const [inviteCode, setInviteCode] = useState("");

  useEffect(() => {
    const urlCode = new URLSearchParams(window.location.search).get("code");
    const storedCode = sessionStorage.getItem("peakrunning_invite_code");
    const code = urlCode ?? storedCode;
    if (code) {
      setInviteCode(code.toUpperCase());
      setMode("join");
      setBuyerLanding(false);
      sessionStorage.removeItem("peakrunning_invite_code");
      if (urlCode) {
        window.history.replaceState({}, "", window.location.pathname);
      }
    }
  }, []);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState<string | null>(null);

  const [squadLookup, setSquadLookup] = useState<SquadLookup | null>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);

  const invalidateCurrentUser = () => {
    queryClient.invalidateQueries({ queryKey: getGetCurrentUserQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListSquadsQueryKey() });
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !squadName.trim()) return;
    setError("");
    createSquad.mutate(
      {
        data: {
          name: squadName.trim(),
          creatorName: name.trim(),
          goalRace: goalRace.trim() || null,
        },
      },
      {
        onSuccess: () => {
          invalidateCurrentUser();
          setLocation("/dashboard");
        },
        onError: () => {
          setError("Something went wrong. Please try again.");
        },
      }
    );
  };

  const handleJoin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !inviteCode.trim()) return;
    setError("");
    joinSquad.mutate(
      {
        data: {
          inviteCode: inviteCode.trim().toUpperCase(),
          name: name.trim(),
          goalRace: goalRace.trim() || null,
        },
      },
      {
        onSuccess: (data) => {
          if (data.welcomeMessage) {
            setWelcomeMessage(data.welcomeMessage);
            setMode("welcome");
          } else {
            invalidateCurrentUser();
            setLocation("/dashboard");
          }
        },
        onError: () => {
          setError("Invalid invite code. Check with your captain and try again.");
        },
      }
    );
  };

  const handleLookupSquad = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;
    setError("");
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/squads/by-invite/${inviteCode.trim().toUpperCase()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setError("Squad not found. Check the invite code and try again.");
        return;
      }
      const data: SquadLookup = await res.json();
      if (data.unclaimedMembers.length === 0) {
        setError("No unclaimed profiles in this squad. Use 'Join a Squad' to create a new profile instead.");
        return;
      }
      setSquadLookup(data);
      setMode("claim-pick");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClaimProfile = async () => {
    if (!selectedMemberId || !squadLookup) return;
    setError("");
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/me/claim-profile", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ memberId: selectedMemberId, inviteCode: inviteCode.trim().toUpperCase() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error ?? "Failed to claim profile. Please try again.");
        return;
      }
      invalidateCurrentUser();
      setLocation("/dashboard");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="mb-8 text-center">
          <span className="font-display text-2xl font-bold tracking-tight">
            PEAK<span className="text-primary">RUNNING</span>
          </span>
          {buyerLanding && mode === "create" ? (
            <>
              <h1 className="font-display text-xl font-bold mt-4 mb-1">Create your squad</h1>
              <p className="text-muted-foreground text-sm">
                Your plan is active. Name your squad to get started.
              </p>
            </>
          ) : (
            <>
              <h1 className="font-display text-xl font-bold mt-4 mb-1">Set up your runner profile</h1>
              <p className="text-muted-foreground text-sm">
                Create a squad, join one, or claim an existing profile.
              </p>
            </>
          )}
        </div>

        {mode === "choose" && (
          <div className="space-y-3">
            <button
              onClick={() => setMode("create")}
              className="w-full border border-border rounded p-5 text-left hover:border-primary/50 hover:bg-accent/20 transition-all group"
            >
              <div className="font-display font-bold text-base mb-1">Create a Squad</div>
              <p className="text-muted-foreground text-sm">
                Start a new squad and invite your runners.
              </p>
            </button>
            <button
              onClick={() => setMode("join")}
              className="w-full border border-border rounded p-5 text-left hover:border-primary/50 hover:bg-accent/20 transition-all group"
            >
              <div className="font-display font-bold text-base mb-1">Join a Squad</div>
              <p className="text-muted-foreground text-sm">
                Have an invite code? Join your squad now.
              </p>
            </button>
            <button
              onClick={() => setMode("claim")}
              className="w-full border border-border rounded p-5 text-left hover:border-primary/50 hover:bg-accent/20 transition-all group"
            >
              <div className="font-display font-bold text-base mb-1">I Already Have a Profile</div>
              <p className="text-muted-foreground text-sm">
                Your captain added you — connect your account to your existing runner profile.
              </p>
            </button>
          </div>
        )}

        {mode === "create" && (
          <div className="bg-card border border-border rounded p-6">
            {!buyerLanding && (
              <>
                <button
                  onClick={() => { setMode("choose"); setError(""); }}
                  className="text-xs text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1"
                >
                  ← Back
                </button>
                <h2 className="font-display text-lg font-bold mb-5">Create a Squad</h2>
              </>
            )}
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="pr-label">
                  Your Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Alex Chen"
                  autoFocus
                  className="pr-field"
                />
              </div>
              <div>
                <label className="pr-label">
                  Squad Name *
                </label>
                <input
                  type="text"
                  value={squadName}
                  onChange={(e) => setSquadName(e.target.value)}
                  placeholder="5am Crew"
                  className="pr-field"
                />
              </div>
              <div>
                <label className="pr-label">
                  Goal Race (optional)
                </label>
                <input
                  type="text"
                  value={goalRace}
                  onChange={(e) => setGoalRace(e.target.value)}
                  placeholder="Boston Marathon 2026"
                  className="pr-field"
                />
              </div>
              {error && <p className="text-destructive text-sm">{error}</p>}
              <button
                type="submit"
                disabled={!name.trim() || !squadName.trim() || createSquad.isPending}
                className="w-full bg-primary text-primary-foreground rounded py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50 mt-2"
              >
                {createSquad.isPending ? "Creating..." : "Create Squad"}
              </button>
            </form>
            {buyerLanding && (
              <button
                onClick={() => { setBuyerLanding(false); setMode("choose"); setError(""); }}
                className="w-full text-center text-xs text-muted-foreground hover:text-foreground mt-4"
              >
                Joining someone else's squad instead?
              </button>
            )}
          </div>
        )}

        {mode === "join" && (
          <div className="bg-card border border-border rounded p-6">
            <button
              onClick={() => { setMode("choose"); setError(""); }}
              className="text-xs text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1"
            >
              ← Back
            </button>
            <h2 className="font-display text-lg font-bold mb-5">Join a Squad</h2>
            <form onSubmit={handleJoin} className="space-y-4">
              <div>
                <label className="pr-label">
                  Your Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Alex Chen"
                  autoFocus
                  className="pr-field"
                />
              </div>
              <div>
                <label className="pr-label">
                  Invite Code *
                </label>
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  placeholder="A1B2C3D4"
                  className="pr-field font-mono"
                />
              </div>
              <div>
                <label className="pr-label">
                  Goal Race (optional)
                </label>
                <input
                  type="text"
                  value={goalRace}
                  onChange={(e) => setGoalRace(e.target.value)}
                  placeholder="Boston Marathon 2026"
                  className="pr-field"
                />
              </div>
              {error && <p className="text-destructive text-sm">{error}</p>}
              <button
                type="submit"
                disabled={!name.trim() || !inviteCode.trim() || joinSquad.isPending}
                className="w-full bg-primary text-primary-foreground rounded py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50 mt-2"
              >
                {joinSquad.isPending ? "Joining..." : "Join Squad"}
              </button>
            </form>
          </div>
        )}

        {mode === "claim" && (
          <div className="bg-card border border-border rounded p-6">
            <button
              onClick={() => { setMode("choose"); setError(""); setInviteCode(""); }}
              className="text-xs text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1"
            >
              ← Back
            </button>
            <h2 className="font-display text-lg font-bold mb-2">Claim Existing Profile</h2>
            <p className="text-muted-foreground text-sm mb-5">
              Enter your squad's invite code and we'll show you the existing profiles to connect to.
            </p>
            <form onSubmit={handleLookupSquad} className="space-y-4">
              <div>
                <label className="pr-label">
                  Squad Invite Code *
                </label>
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  placeholder="A1B2C3D4"
                  autoFocus
                  className="pr-field font-mono"
                />
              </div>
              {error && <p className="text-destructive text-sm">{error}</p>}
              <button
                type="submit"
                disabled={!inviteCode.trim() || loading}
                className="w-full bg-primary text-primary-foreground rounded py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50 mt-2"
              >
                {loading ? "Looking up..." : "Find Squad"}
              </button>
            </form>
          </div>
        )}

        {mode === "welcome" && welcomeMessage && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded p-6 text-center"
          >
            <div className="mb-4">
              <span className="text-3xl">👋</span>
            </div>
            <h2 className="font-display text-xl font-bold mb-3">Welcome to the squad!</h2>
            <p className="text-muted-foreground text-sm leading-relaxed whitespace-pre-wrap mb-6">
              {welcomeMessage}
            </p>
            <button
              onClick={() => {
                invalidateCurrentUser();
                setLocation("/dashboard");
              }}
              className="w-full bg-primary text-primary-foreground rounded py-2.5 text-sm font-semibold hover:opacity-90"
            >
              Let's go →
            </button>
          </motion.div>
        )}

        {mode === "claim-pick" && squadLookup && (
          <div className="bg-card border border-border rounded p-6">
            <button
              onClick={() => { setMode("claim"); setError(""); setSquadLookup(null); setSelectedMemberId(null); }}
              className="text-xs text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1"
            >
              ← Back
            </button>
            <h2 className="font-display text-lg font-bold mb-1">Select Your Profile</h2>
            <p className="text-muted-foreground text-sm mb-5">
              Squad: <span className="text-foreground font-medium">{squadLookup.squad.name}</span>
            </p>
            <div className="space-y-2 mb-5">
              {squadLookup.unclaimedMembers.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setSelectedMemberId(m.id)}
                  className={`w-full border rounded p-3 text-left transition-all ${
                    selectedMemberId === m.id
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border hover:border-primary/40 hover:bg-accent/20"
                  }`}
                >
                  <span className="font-medium text-sm">{m.name}</span>
                </button>
              ))}
            </div>
            {error && <p className="text-destructive text-sm mb-3">{error}</p>}
            <button
              onClick={handleClaimProfile}
              disabled={!selectedMemberId || loading}
              className="w-full bg-primary text-primary-foreground rounded py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {loading ? "Connecting..." : "Connect to This Profile"}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
